import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getUserSubscription } from "../services/subscription";
import { toast } from "sonner";
import styles from "../styles/account.module.css";
import {
  ArrowLeft,
  User,
  CreditCard,
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle,
  LogOut,
  Star,
} from "react-feather";

const Account = () => {
  const { user, isAuthenticated, signOut } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect if not authenticated
    if (!isAuthenticated) {
      navigate("/auth");
      return;
    }

    const fetchSubscription = async () => {
      try {
        setIsLoading(true);
        const subscriptionData = await getUserSubscription(user.id);
        setSubscription(subscriptionData);
      } catch (error) {
        console.error("Error fetching subscription:", error);
        toast.error("Failed to load subscription information");
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchSubscription();
    }
  }, [isAuthenticated, user, navigate]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (error) {
      toast.error("Failed to sign out");
    }
  };

  // Cancellation workflow removed in favor of managing via Stripe Customer Portal

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getPlanName = () => {
    if (!subscription) return "No Plan";

    const priceId = subscription.price_id;
    if (!priceId) return "Unknown Plan";

    if (priceId.includes("basic")) return "<Bramlo> Basic";
    if (priceId.includes("plus") && priceId.includes("annual"))
      return "<Bramlo>+ Annual";
    if (priceId.includes("plus")) return "<Bramlo>";

    return "Premium Plan";
  };

  const getFormattedPrice = () => {
    if (!subscription || !subscription.amount) return "N/A";

    const amount = subscription.amount / 100; // Convert cents to dollars
    const currency = subscription.currency || "usd";

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  const getSubscriptionStatus = () => {
    if (!subscription) return "No subscription";

    const status = subscription.status;
    switch (status) {
      case "active":
        return "Active";
      case "trialing":
        return "Trial";
      case "canceled":
        return "Cancelled";
      case "past_due":
        return "Past Due";
      default:
        return status || "Unknown";
    }
  };

  const getStatusColor = () => {
    if (!subscription) return styles.statusNone;

    const status = subscription.status;
    switch (status) {
      case "active":
        return styles.statusActive;
      case "trialing":
        return styles.statusTrialing;
      case "canceled":
        return styles.statusCanceled;
      case "past_due":
        return styles.statusPastDue;
      default:
        return styles.statusUnknown;
    }
  };

  return (
    <div className={styles.accountContainer}>
      <div className={styles.content}>
        <Link to="/" className={styles.backButton}>
          <ArrowLeft size={18} />
          Back
        </Link>

        <button onClick={handleSignOut} className={styles.signOutButton}>
          Sign Out <LogOut size={18} />
        </button>

        <header className={styles.header}>
          <h1 className={styles.title}>Account Settings</h1>
          <p className={styles.subtitle}>
            Manage your account details and subscription
          </p>
        </header>

        <div className={styles.accountGrid}>
          <section className={styles.profileSection}>
            <h2 className={styles.sectionTitle}>Profile</h2>
            <div className={styles.profileCard}>
              <div className={styles.profileHeader}>
                <div className={styles.profileAvatar}>
                  <User size={24} />
                </div>
                <div className={styles.profileInfo}>
                  <h3>{user?.email}</h3>
                  <p>
                    Member since{" "}
                    {user?.created_at
                      ? new Date(user.created_at).toLocaleDateString()
                      : "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className={styles.subscriptionSection}>
            <h2 className={styles.sectionTitle}>Subscription</h2>

            {isLoading ? (
              <div className={styles.loadingState}>
                Loading subscription information...
              </div>
            ) : (
              <div className={styles.subscriptionCard}>
                <div className={styles.subscriptionHeader}>
                  <div className={styles.planBadge}>
                    <Star size={16} />
                    <span>{getPlanName()}</span>
                  </div>
                  <div className={`${styles.statusBadge} ${getStatusColor()}`}>
                    {subscription?.status === "active" ||
                    subscription?.status === "trialing" ? (
                      <CheckCircle size={16} />
                    ) : (
                      <AlertCircle size={16} />
                    )}
                    <span>{getSubscriptionStatus()}</span>
                  </div>
                </div>

                {subscription &&
                (subscription.status === "active" ||
                  subscription.status === "trialing") ? (
                  <>
                    <div className={styles.subscriptionDetails}>
                      {subscription?.amount &&
                      subscription?.current_period_start &&
                      subscription?.current_period_end ? (
                        <>
                          <div className={styles.detailItem}>
                            <CreditCard size={16} />
                            <div>
                              <span className={styles.detailLabel}>Price</span>
                              <span className={styles.detailValue}>
                                {getFormattedPrice()}/
                                {subscription.interval || "month"}
                              </span>
                            </div>
                          </div>

                          <div className={styles.detailItem}>
                            <Calendar size={16} />
                            <div>
                              <span className={styles.detailLabel}>
                                Current Period
                              </span>
                              <span className={styles.detailValue}>
                                {formatDate(subscription.current_period_start)}{" "}
                                - {formatDate(subscription.current_period_end)}
                              </span>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className={styles.detailItem}>
                          <AlertCircle size={16} />
                          <div>
                            <span className={styles.detailLabel}>Account</span>
                            <span className={styles.detailValue}>
                              Thanks for being part of our community. If
                              anything comes up—billing, feedback, or
                              ideas—please{" "}
                              <Link
                                to="/contact"
                                className={styles.gradientLink}
                              >
                                get in touch
                              </Link>
                              .
                            </span>
                          </div>
                        </div>
                      )}

                      {subscription.cancel_at_period_end && (
                        <div className={styles.detailItem}>
                          <Clock size={16} />
                          <div>
                            <span className={styles.detailLabel}>
                              Cancellation
                            </span>
                            <span className={styles.detailValue}>
                              Your subscription will end on{" "}
                              {formatDate(subscription.current_period_end)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className={styles.subscriptionActions}>
                      <a
                        href="https://billing.stripe.com/p/login/8x2cN64TT7kZfdpaexcQU00"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.changePlanButton}
                        aria-label="Manage your subscription and invoices in Stripe"
                      >
                        Manage Subscription
                      </a>
                    </div>
                  </>
                ) : (
                  <div className={styles.noSubscriptionState}>
                    <p>You don't have an active subscription.</p>
                    <a
                      href="https://billing.stripe.com/p/login/8x2cN64TT7kZfdpaexcQU00"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.subscribeCta}
                      aria-label="Manage your subscription and invoices in Stripe"
                    >
                      Manage Subscription
                    </a>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default Account;
